import type { FragmentType } from 'src/gql';
import { BriefcaseBusiness, CreditCard, GraduationCap, ReceiptText, Store, Truck } from 'lucide-react';
import { isLiabilityTypeName, LIABILITY_TYPE_DISPLAY_NAME } from 'src/constants/account-link';
import { graphql, unmaskFragment } from 'src/gql';

import type { PortfolioItemTypeName } from '@savvy/constants/generated/gql-enums';
import {
  LIABILITY_TYPE_AUTO,
  LIABILITY_TYPE_COMMERCIAL,
  LIABILITY_TYPE_CONSUMER,
  LIABILITY_TYPE_GENERAL,
  LIABILITY_TYPE_HOME_EQUITY,
  LIABILITY_TYPE_LINE_OF_CREDIT,
  LIABILITY_TYPE_MORTGAGE,
  LIABILITY_TYPE_OTHER,
  LIABILITY_TYPE_STUDENT,
} from '@savvy/constants/liability-types';
import { financialAccountIconColors } from '@savvy/deprecated-shared/theme';
import defaultGuard from '@savvy/utils/default-guard';

import { formatItemValue, renderItemIcon } from './portfolio-item-display';

export const RenderIconForLiabilityFragment = graphql(/* GraphQL */ `
  fragment RenderIconForLiability on Liability {
    id
    type
  }
`);

export function renderIconForLiability(liabilityFrag: FragmentType<typeof RenderIconForLiabilityFragment>) {
  const { type, id } = unmaskFragment(RenderIconForLiabilityFragment, liabilityFrag);
  if (!isLiabilityTypeName(type)) {
    // eslint-disable-next-line @next/next/no-img-element -- fallback avatar is generated by a remote avatar service
    return <img src={`https://avatar.tobi.sh/Liability${id}?size=30`} />;
  }
  switch (type) {
    case LIABILITY_TYPE_AUTO:
      return renderItemIcon(financialAccountIconColors.liability, <Truck color="#fff" size={20} />);
    case LIABILITY_TYPE_COMMERCIAL:
      return renderItemIcon(financialAccountIconColors.liability, <BriefcaseBusiness color="#fff" size={20} />);
    case LIABILITY_TYPE_CONSUMER:
      return renderItemIcon(financialAccountIconColors.liability, <Store color="#fff" size={20} />);
    case LIABILITY_TYPE_LINE_OF_CREDIT:
      return renderItemIcon(financialAccountIconColors.liability, <ReceiptText color="#fff" size={20} />);
    case LIABILITY_TYPE_STUDENT:
      return renderItemIcon(financialAccountIconColors.liability, <GraduationCap color="#fff" size={20} />);
    case LIABILITY_TYPE_GENERAL:
    case LIABILITY_TYPE_HOME_EQUITY:
    case LIABILITY_TYPE_MORTGAGE:
    case LIABILITY_TYPE_OTHER:
      return renderItemIcon(financialAccountIconColors.liability, <CreditCard color="#fff" size={20} />);
    default:
      return defaultGuard(
        type,
        renderItemIcon(financialAccountIconColors.liability, <CreditCard color="#fff" size={20} />),
      );
  }
}

export const LIABILITY_DELETION_TEXT =
  'When you delete a liability it will no longer appear in your list of accounts, assets, and liabilities. Historical values of this liability will be removed from your net worth graph.';

export const LIABILITY_DELETE_BUTTON_TEXT = 'Delete liability';

export function liabilityTitle({ liabilityName }: { liabilityName: string }) {
  return liabilityName;
}

export function liabilitySubtitle(liabilityType: PortfolioItemTypeName) {
  if (!isLiabilityTypeName(liabilityType)) {
    return liabilityType;
  }
  return LIABILITY_TYPE_DISPLAY_NAME[isLiabilityTypeName(liabilityType) ? liabilityType : LIABILITY_TYPE_OTHER];
}

export function liabilityValueTitle(liability: { currentBalance: { amount: string } }) {
  return formatItemValue(-Number(liability.currentBalance.amount));
}
